import ItemCover from "components/ItemCover"
import PageLoader from "components/PageLoader"
import { ProductCardPreview } from "components/ProductCardPreview"
import ProductColors from "components/ProductColors"
import { SearchProductModal } from "components/SearchProductModal"
import { useCurrentId } from "hooks/useCurrentId"
import { useDebounce } from "hooks/useDebounce"
import { type ChangeEvent, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, Link } from "react-router-dom"
import { getTintVTOService } from "services/tintvto"
import { formatProductTypeName } from "utils/formatProductTypeName"
import { ArrowLeft, Check, Plus, Trash } from "lucide-react"
import { useAlert } from "providers/AlertProvider"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

interface LookPageProps {
  add?: boolean
}

export const LookPage = ({ add }: LookPageProps) => {
  const { t } = useTranslation()
  const service = getTintVTOService()
  const navigate = useNavigate()
  const lookId = useCurrentId()
  const [lookItem, setLookItem] = useState<any>()
  const [loading, setLoading] = useState(false)
  const [newProducts, setNewProducts] = useState([])
  const [loadingNewProducts, setLoadingNewProducts] = useState(false)
  const [searchText, setSearchText] = useState("")
  const debouncedSearchText = useDebounce<string>(searchText, 300)
  const [coverType, setCoverType] = useState("Custom Model")
  const [isModalOpen, setModalOpen] = useState(false)
  const { showAlert } = useAlert()
  useEffect(() => {
    const fetchLook = async () => {
      if (lookId && !add) {
        const { data } = await service.lookById(lookId)
        setLookItem(data.look)
      } else {
        setLookItem({
          id: "",
          name: "",
          cover: "",
          products: {
            products: [],
            selectedSkus: [],
          },
        })
      }
    }

    fetchLook()
  }, [lookId, add, service])

  useEffect(() => {
    if (debouncedSearchText === "") {
      setNewProducts([])
    } else {
      const fetchProducts = async () => {
        setLoadingNewProducts(true)
        const { data } = await service.searchProducts(debouncedSearchText)
        setNewProducts(data.searchProducts)
        setLoadingNewProducts(false)
      }

      fetchProducts()
    }
  }, [debouncedSearchText, service])

  const handleSaveItem = async () => {
    setLoading(true)
    const response = await service.saveLook(lookItem)
    const error = response.errors ? response.errors[0] : false

    showAlert({
      error: !!error,
      text: error
        ? getGenericUserErrorMessage(t)
        : t(add ? "lookPage.lookCreated" : "lookPage.lookUpdated", { lookName: lookItem.name }),
    })

    setLoading(false)

    navigate(`/looks`)
  }

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name
    setLookItem({
      ...lookItem,
      [name]: e.target.value,
    })
  }

  const addProduct = (p: any, sku: string) => {
    lookItem.products.products.push(p)
    lookItem.products.selectedSkus.push(sku)
    setLookItem({
      ...lookItem,
      products: {
        products: lookItem.products.products,
        selectedSkus: lookItem.products.selectedSkus,
      },
    })
  }

  const deleteProduct = (forDelete: any) => {
    const updatedProducts = lookItem.products.products.filter(
      (p: any) => p.id !== forDelete.productId
    )
    const updatedSkus = lookItem.products.selectedSkus.filter((s: any) => s.id !== forDelete.id)

    setLookItem({
      ...lookItem,
      products: {
        products: updatedProducts,
        selectedSkus: updatedSkus,
      },
    })
  }

  const imagePlaceHolder = "https://placehold.co/400x400?text=Look+Cover+Image"

  return (
    <div data-testid={add ? "look-add-page" : "look-edit-page"}>
      <header className="px-6 py-4" data-testid="look-editor-header">
        <Link
          to="/looks"
          data-testid="look-editor-back"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("lookPage.backToLooks")}
        </Link>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">
            {add ? t("lookPage.addLook") : t("lookPage.editLook")}
          </h1>
          <div className="flex gap-2">
            <Button variant="outline" asChild data-testid="look-editor-cancel">
              <a href="/looks">{t("lookPage.cancel")}</a>
            </Button>
            <Button
              data-testid="look-editor-save"
              onClick={handleSaveItem}
              disabled={loading || !lookItem?.name || lookItem.products.products.length < 2}
            >
              {loading ? (
                <span className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {t("lookPage.save")}
            </Button>
          </div>
        </div>
      </header>

      <div className="px-6 pb-6 w-full" data-testid="look-editor-content">
        {lookItem ? (
          <div className="grid grid-cols-[2fr_2fr_1fr] gap-4">
            <Card className="flex flex-col gap-4 max-h-[340px]" data-testid="look-info-card">
              <CardHeader>
                <CardTitle>{t("lookPage.lookCardInfo")}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <div>
                  <Label htmlFor="name">{t("lookPage.lookName")}</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="look-name-input"
                    onChange={handleInputChange}
                    value={lookItem.name}
                    required
                  />
                </div>
                <div>
                  <Label>{t("lookPage.coverType")}</Label>
                  <Select value={coverType} onValueChange={setCoverType}>
                    <SelectTrigger data-testid="look-cover-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent data-testid="look-cover-type-options">
                      <SelectItem value="Custom Model" data-testid="look-cover-type-option">
                        {t("lookPage.customModel")}
                      </SelectItem>
                      <SelectItem value="Other Type" data-testid="look-cover-type-option">
                        {t("lookPage.otherType")}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card
              className="flex flex-col gap-4 overflow-y-auto w-[calc(100vw-980px)] min-w-[400px] h-[calc(100vh-200px)]"
              data-testid="look-products-card"
            >
              <CardHeader>
                <div className="flex items-center">
                  <CardTitle>{t("lookPage.lookProductItems")}</CardTitle>
                  <Button
                    className="ml-auto"
                    variant="outline"
                    data-testid="look-add-product"
                    onClick={() => setModalOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                    {t("lookPage.addProduct")}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <table className="w-full border-collapse" data-testid="look-products-table">
                  <thead>
                    <tr className="border-b">
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        {t("lookPage.logo")}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        {t("lookPage.name")}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        {t("lookPage.type")}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                        {t("lookPage.colors")}
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lookItem.products.selectedSkus.map((sku: any, index: number) => {
                      const product = lookItem.products.products.find(
                        (p: any) => p.id === sku.productId
                      )
                      return (
                        <tr key={index} className="border-b" data-testid="look-product-row">
                          <td className="px-4 py-3">
                            <div className="p-1">
                              <ItemCover cover={product?.cover} />
                            </div>
                          </td>
                          <td className="px-4 py-3 max-w-[178px] whitespace-nowrap overflow-hidden text-ellipsis">
                            {product?.name}
                          </td>
                          <td className="px-4 py-3">
                            {product?.type ? formatProductTypeName(product.type) : ""}
                          </td>
                          <td className="px-4 py-3">
                            <ProductColors product={{ skus: [sku] }} size={32} />
                          </td>
                          <td className="px-4 py-3">
                            <Button
                              variant="ghost"
                              size="icon"
                              data-testid="look-product-delete"
                              onClick={() => deleteProduct(sku)}
                              aria-label={t("lookPage.delete")}
                            >
                              <Trash className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <Card
              className="flex flex-col gap-4 items-center justify-center h-[calc(100vh-200px)] min-h-[400px]"
              data-testid="look-preview-card"
            >
              <CardContent className="flex items-center justify-center h-full">
                <ProductCardPreview
                  imageSrc={lookItem.cover ? lookItem.cover : imagePlaceHolder}
                  sku=""
                  brand=""
                  productName={lookItem?.name ?? ""}
                  color=""
                />
              </CardContent>
            </Card>

            <SearchProductModal
              isModalOpen={isModalOpen}
              setModalOpen={setModalOpen}
              newProducts={newProducts}
              loadingNewProducts={loadingNewProducts}
              debouncedSearchText={debouncedSearchText}
              searchText={searchText}
              setSearchText={setSearchText}
              addProduct={addProduct}
            />
          </div>
        ) : (
          <PageLoader />
        )}
      </div>
    </div>
  )
}
